import type { Command } from "commander";
import type { CliContainer } from "../container.js";
import type { ProviderContext, ProviderService } from "../service-registry.js";
import type { ConfiguredServiceMetadata } from "../../services/config.js";
import { unconfigureService, loadConfiguredServices } from "../../services/config.js";
import { createMutationReporter } from "../../services/mutation-events.js";
import { resolveIsolatedTargetDirectory } from "../isolated-env.js";
import {
  buildProviderContext,
  createExecutionResources,
  resolveCommandFlags,
  resolveServiceAdapter,
  formatServiceList,
  listServiceNames,
  type CommandFlags,
  type ExecutionResources
} from "./shared.js";
import { confirmDestructive } from "./confirm-destructive.js";
import { createOverlayFileSystem } from "./configure.js";

export interface UnconfigureCommandOptions {
  configName?: string;
  /** Set by callers that already confirmed a wider blast radius, e.g. `logout`. */
  alreadyConfirmed?: boolean;
}

export function registerUnconfigureCommand(program: Command, container: CliContainer): Command {
  const serviceNames = listServiceNames(
    container.registry.list().filter((service) => service.supportsConfigure !== false)
  );
  const serviceDescription = `Agent to unconfigure${formatServiceList(serviceNames)}`;
  return program
    .command("unconfigure")
    .alias("uc")
    .description(
      "Danger: removes the agent's Poe API tooling configuration files. Requires --yes to run non-interactively; preview with --dry-run."
    )
    .argument("<agent>", serviceDescription)
    .action(async (service: string, options: UnconfigureCommandOptions) => {
      await executeUnconfigure(program, container, service, options);
    });
}

export async function executeUnconfigure(
  program: Command,
  container: CliContainer,
  service: string,
  options: UnconfigureCommandOptions
): Promise<void> {
  const adapter = resolveServiceAdapter(container, service);
  if (adapter.supportsConfigure === false) {
    throw new Error(`${adapter.label} is spawn-only and does not support unconfigure.`);
  }
  const canonicalService = adapter.name;
  const flags = resolveCommandFlags(program);
  const resources = createExecutionResources(container, flags, `unconfigure:${canonicalService}`);

  resources.logger.intro(`unconfigure ${canonicalService}`);

  const providerContext = buildProviderContext(container, adapter, resources);
  const configuredServices = await loadConfiguredServices({
    fs: container.fs,
    filePath: providerContext.env.configPath,
    projectFilePath: providerContext.env.projectConfigPath,
    readOnly: flags.dryRun
  });
  const metadata = configuredServices[canonicalService];
  if (!metadata) {
    resources.context.complete(formatUnconfigureMessages(adapter, false));
    resources.context.finalize();
    return;
  }

  if (options.alreadyConfirmed !== true) {
    await confirmDestructive({
      logger: resources.logger,
      flags,
      action: `unconfigure ${canonicalService}`,
      summary: [
        `Removes ${adapter.configurationLabel ?? adapter.label} configuration${
          metadata.files && metadata.files.length > 0 ? `: ${metadata.files.join(", ")}` : "."
        }`
      ],
      message: `Remove ${adapter.configurationLabel ?? adapter.label} configuration?`
    });
  }

  const unconfigured = await unconfigureAgent({
    container,
    adapter,
    flags,
    resources,
    metadata
  });

  resources.context.complete(formatUnconfigureMessages(adapter, unconfigured, metadata.files));

  resources.context.finalize();
}

export interface UnconfigureAgentInput {
  container: CliContainer;
  adapter: ProviderService;
  flags: CommandFlags;
  resources: ExecutionResources;
  metadata: ConfiguredServiceMetadata;
}

/**
 * Removes one agent's configuration without rendering any panel of its own, so
 * callers that unconfigure several agents (e.g. `logout`) can report the whole
 * batch inside a single panel.
 */
export async function unconfigureAgent(input: UnconfigureAgentInput): Promise<boolean> {
  const { container, adapter, flags, resources, metadata } = input;
  const canonicalService = adapter.name;
  const providerContext = buildProviderContext(container, adapter, resources);
  const mutationLogger = createMutationReporter(resources.logger);
  const transaction = flags.dryRun ? undefined : createOverlayFileSystem(providerContext.command.fs);
  const executionProviderContext = transaction
    ? {
        ...providerContext,
        command: {
          ...providerContext.command,
          fs: transaction.fs
        }
      }
    : providerContext;

  const payload = await createUnconfigurePayload({
    context: providerContext,
    metadata
  });

  const result = await container.registry.invoke(
    canonicalService,
    "unconfigure",
    async (entry) => {
      if (!entry.unconfigure) {
        throw new Error(`Agent "${canonicalService}" does not support unconfigure.`);
      }
      const result = await entry.unconfigure(
        {
          fs: executionProviderContext.command.fs,
          env: executionProviderContext.env,
          command: executionProviderContext.command,
          options: payload
        },
        { observers: mutationLogger }
      );

      const isolated = adapter.isolatedEnv;
      if (isolated && isolated.requiresConfig !== false) {
        await entry.unconfigure(
          {
            fs: executionProviderContext.command.fs,
            env: executionProviderContext.env,
            command: executionProviderContext.command,
            options: payload,
            pathMapper: {
              mapTargetDirectory: ({ targetDirectory }: { targetDirectory: string }) =>
                resolveIsolatedTargetDirectory({
                  targetDirectory,
                  isolated,
                  env: executionProviderContext.env,
                  providerName: adapter.name
                })
            }
          },
          { observers: mutationLogger }
        );
      }

      if (!flags.dryRun) {
        await unconfigureService({
          fs: executionProviderContext.command.fs,
          filePath: providerContext.env.configPath,
          projectFilePath: providerContext.env.projectConfigPath,
          service: canonicalService
        });
      }

      await transaction?.commit();

      return result;
    }
  );

  return typeof result === "boolean" ? result : Boolean(result);
}

interface UnconfigurePayloadInit {
  context: ProviderContext;
  metadata: ConfiguredServiceMetadata;
}

async function createUnconfigurePayload(init: UnconfigurePayloadInit): Promise<unknown> {
  const { context, metadata } = init;
  return {
    env: context.env,
    provider: { id: metadata.provider }
  };
}

export function formatUnconfigureMessages(
  adapter: { label: string; configurationLabel?: string },
  unconfigured: boolean,
  files: readonly string[] = []
): { success: string; dry: string } {
  const label = adapter.configurationLabel ?? adapter.label;
  const detail = files.length > 0 ? `: ${files.join(", ")}` : ".";
  return {
    success: unconfigured ? `Removed ${label} configuration.` : `No ${label} configuration found.`,
    dry: unconfigured
      ? `Dry run: would remove ${label} configuration${detail}`
      : `No ${label} configuration found.`
  };
}
